import React, {Fragment, useEffect, useState} from 'react';
import {Dialog, Transition} from '@headlessui/react';
import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query';
import {toast} from 'react-toastify';
import {X, Loader2, Pencil} from 'lucide-react';
import {
    ProviderPatientsService,
    type ProviderPatientProfile,
    type ProviderPatientUpdate,
} from '@/modules/provider-console/services/providerPatientsService';
import {Ethnicities, States} from '@/shared/services/constants';
import Button from '@/shared/components/Button';

/**
 * Canonical "Edit patient" button used everywhere a provider can open the
 * PatientEditModal. Centralized so all four entry points (queue row,
 * search result, order-builder banner, patient detail header) stay
 * visually identical. Do not inline the styles at call sites — this
 * wraps the shared <Button> primitive from STYLE_GUIDE §5.
 */
export const PatientEditButton: React.FC<{
    onClick: () => void;
    disabled?: boolean;
    title?: string;
}> = ({onClick, disabled, title}) => (
    <Button
        variant="secondary"
        size="sm"
        onClick={onClick}
        disabled={disabled}
        title={title ?? 'Edit patient demographics'}
        aria-label="Edit patient"
        iconLeft={<Pencil className="h-3.5 w-3.5" />}>
        Edit
    </Button>
);

interface Props {
    isOpen: boolean;
    userId: number | null;
    onClose: () => void;
    /** Called after a successful save so the parent can refetch any patient-derived data. */
    onSaved?: (profile: ProviderPatientProfile) => void;
}

/**
 * Standalone "Edit Patient" modal — Dialog wrapper around the shared
 * <PatientEditFormBody>.  Used by entry points that ONLY want the
 * demographic editor (Order Queue, search result, order-builder banner).
 *
 * The combined patient + clinical context modal lives in
 * PatientContextModal.tsx and uses the same form body inside a tab.
 */
const PatientEditModal: React.FC<Props> = ({isOpen, userId, onClose, onSaved}) => (
    <Transition appear show={isOpen} as={Fragment}>
        <Dialog as="div" className="relative z-50" onClose={onClose}>
            <Transition.Child
                as={Fragment}
                enter="ease-out duration-150"
                enterFrom="opacity-0"
                enterTo="opacity-100"
                leave="ease-in duration-100"
                leaveFrom="opacity-100"
                leaveTo="opacity-0">
                <div className="fixed inset-0 bg-black/30" />
            </Transition.Child>

            <div className="fixed inset-0 overflow-y-auto">
                <div className="flex min-h-full items-center justify-center p-4">
                    <Transition.Child
                        as={Fragment}
                        enter="ease-out duration-150"
                        enterFrom="opacity-0 scale-95"
                        enterTo="opacity-100 scale-100"
                        leave="ease-in duration-100"
                        leaveFrom="opacity-100 scale-100"
                        leaveTo="opacity-0 scale-95">
                        <Dialog.Panel className="w-full max-w-2xl rounded-2xl border border-mld-panel-border bg-mld-menu-background text-mld-menu-text p-6 shadow-xl">
                            <div className="mb-4 flex items-center justify-between">
                                <Dialog.Title className="text-lg font-semibold text-mld-menu-text">
                                    Edit Patient
                                </Dialog.Title>
                                <button
                                    type="button"
                                    onClick={onClose}
                                    className="rounded-md p-1 text-mld-muted-text hover:bg-mld-panel-border/30 hover:text-mld-menu-text">
                                    <X className="h-5 w-5" />
                                </button>
                            </div>
                            <PatientEditFormBody
                                isOpen={isOpen}
                                userId={userId}
                                onClose={onClose}
                                onSaved={onSaved}
                            />
                        </Dialog.Panel>
                    </Transition.Child>
                </div>
            </div>
        </Dialog>
    </Transition>
);

const EMPTY_FORM = {
    firstName: '',
    lastName: '',
    birthday: '',
    gender: '',
    ethnicity: '',
    menopausalStatus: '' as '' | 'pre' | 'post',
    address1: '',
    address2: '',
    city: '',
    state: '',
    zip: '',
};

// API returns birthday as a Date-serialized ISO string ("1971-12-14T...") or
// "YYYY-MM-DD". The <input type="date"> expects the latter, so slice the
// date portion off whatever shape we get.
function toDateInput(value: string | null | undefined): string {
    if (!value) return '';
    return value.length >= 10 ? value.slice(0, 10) : value;
}

/**
 * Form body for the patient demographic editor.  Pulled out of
 * PatientEditModal so it can render inside the combined
 * PatientContextModal tabs too without duplicating state/mutation logic.
 *
 * Owns its own data fetching + mutation + form state.  Pass `isOpen` so
 * the form resets when the host modal closes.
 */
export const PatientEditFormBody: React.FC<Props> = ({isOpen, userId, onClose, onSaved}) => {
    const queryClient = useQueryClient();
    const [form, setForm] = useState(EMPTY_FORM);

    const profileQuery = useQuery({
        queryKey: ['provider-patient-profile', userId],
        queryFn: () => ProviderPatientsService.getProfile(userId as number),
        enabled: isOpen && userId != null,
        staleTime: 0,
    });

    useEffect(() => {
        if (!profileQuery.data) return;
        const p = profileQuery.data;
        setForm({
            firstName: p.firstName ?? '',
            lastName: p.lastName ?? '',
            birthday: toDateInput(p.birthday),
            gender: p.gender ?? '',
            ethnicity: p.ethnicity ?? '',
            menopausalStatus: (p.menopausalStatus ?? '') as '' | 'pre' | 'post',
            address1: p.address1 ?? '',
            address2: p.address2 ?? '',
            city: p.city ?? '',
            state: p.state ?? '',
            zip: p.zip ?? '',
        });
    }, [profileQuery.data]);

    useEffect(() => {
        if (!isOpen) setForm(EMPTY_FORM);
    }, [isOpen]);

    const saveMutation = useMutation({
        mutationFn: (updates: ProviderPatientUpdate) =>
            ProviderPatientsService.updateProfile(userId as number, updates),
        onSuccess: (saved) => {
            toast.success('Patient updated');
            queryClient.invalidateQueries({queryKey: ['provider-patient-profile', userId]});
            queryClient.invalidateQueries({queryKey: ['order-tests-queue']});
            onSaved?.(saved);
            onClose();
        },
        onError: (err: unknown) => {
            const message =
                err && typeof err === 'object' && 'message' in err
                    ? String((err as {message?: unknown}).message ?? '')
                    : '';
            toast.error(message || 'Failed to update patient');
        },
    });

    const handleSave = () => {
        if (!userId) return;
        const original = profileQuery.data;

        const updates: ProviderPatientUpdate = {};
        const norm = (v: string) => v.trim();
        const eq = (a: string, b: string | null | undefined) => norm(a) === ((b ?? '').toString());

        if (!eq(form.firstName, original?.firstName)) updates.firstName = norm(form.firstName);
        if (!eq(form.lastName, original?.lastName)) updates.lastName = norm(form.lastName);
        if (!eq(form.birthday, toDateInput(original?.birthday))) {
            updates.birthday = form.birthday ? form.birthday : null;
        }
        if (!eq(form.gender, original?.gender)) updates.gender = form.gender || null;
        if (!eq(form.ethnicity, original?.ethnicity)) updates.ethnicity = form.ethnicity || null;
        const effectiveMenopausal: 'pre' | 'post' | null =
            form.gender === 'F' && (form.menopausalStatus === 'pre' || form.menopausalStatus === 'post')
                ? form.menopausalStatus
                : null;
        if (effectiveMenopausal !== (original?.menopausalStatus ?? null)) {
            updates.menopausalStatus = effectiveMenopausal;
        }
        if (!eq(form.address1, original?.address1)) updates.address1 = form.address1 || null;
        if (!eq(form.address2, original?.address2)) updates.address2 = form.address2 || null;
        if (!eq(form.city, original?.city)) updates.city = form.city || null;
        if (!eq(form.state, original?.state)) updates.state = form.state || null;
        if (!eq(form.zip, original?.zip)) updates.zip = form.zip || null;

        if (Object.keys(updates).length === 0) {
            toast.info('No changes to save');
            return;
        }

        const missing: string[] = [];
        if (!norm(form.firstName)) missing.push('first name');
        if (!norm(form.lastName)) missing.push('last name');
        if (!form.birthday) missing.push('date of birth');
        if (!form.gender) missing.push('gender');
        if (missing.length > 0) {
            toast.error(`Cannot save: ${missing.join(', ')} required`);
            return;
        }

        saveMutation.mutate(updates);
    };

    const isLoading = profileQuery.isLoading;
    const isSaving = saveMutation.isPending;

    if (isLoading) {
        return (
            <div className="flex items-center justify-center py-12 text-mld-muted-text">
                <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Loading patient...
            </div>
        );
    }

    return (
        <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
                <Field label="First name" required>
                    <input
                        type="text"
                        value={form.firstName}
                        onChange={(e) => setForm((f) => ({...f, firstName: e.target.value}))}
                        className={inputCls}
                    />
                </Field>
                <Field label="Last name" required>
                    <input
                        type="text"
                        value={form.lastName}
                        onChange={(e) => setForm((f) => ({...f, lastName: e.target.value}))}
                        className={inputCls}
                    />
                </Field>
            </div>

            <div className="grid grid-cols-2 gap-3">
                <Field label="Date of birth" required>
                    <input
                        type="date"
                        value={form.birthday}
                        onChange={(e) => setForm((f) => ({...f, birthday: e.target.value}))}
                        className={inputCls}
                    />
                </Field>
                <Field label="Gender" required>
                    <select
                        value={form.gender}
                        onChange={(e) => setForm((f) => ({...f, gender: e.target.value}))}
                        className={inputCls}>
                        <option value="">Select...</option>
                        <option value="M">Male</option>
                        <option value="F">Female</option>
                    </select>
                </Field>
            </div>

            <div className={form.gender === 'F' ? 'grid grid-cols-2 gap-3' : ''}>
                <Field label="Ethnicity">
                    <select
                        value={form.ethnicity}
                        onChange={(e) => setForm((f) => ({...f, ethnicity: e.target.value}))}
                        className={inputCls}>
                        <option value="">Not specified</option>
                        {Ethnicities.map((eth) => (
                            <option key={eth} value={eth}>
                                {eth}
                            </option>
                        ))}
                    </select>
                </Field>

                {form.gender === 'F' && (
                    <Field label="Menopausal status">
                        <select
                            value={form.menopausalStatus}
                            onChange={(e) =>
                                setForm((f) => ({
                                    ...f,
                                    menopausalStatus: e.target.value as '' | 'pre' | 'post',
                                }))
                            }
                            className={inputCls}>
                            <option value="">Not specified</option>
                            <option value="pre">Pre-menopausal</option>
                            <option value="post">Post-menopausal</option>
                        </select>
                    </Field>
                )}
            </div>

            <Field label="Address line 1">
                <input
                    type="text"
                    value={form.address1}
                    onChange={(e) => setForm((f) => ({...f, address1: e.target.value}))}
                    className={inputCls}
                />
            </Field>

            <Field label="Address line 2">
                <input
                    type="text"
                    value={form.address2}
                    onChange={(e) => setForm((f) => ({...f, address2: e.target.value}))}
                    className={inputCls}
                />
            </Field>

            <div className="grid grid-cols-3 gap-3">
                <Field label="City">
                    <input
                        type="text"
                        value={form.city}
                        onChange={(e) => setForm((f) => ({...f, city: e.target.value}))}
                        className={inputCls}
                    />
                </Field>
                <Field label="State">
                    <select
                        value={form.state}
                        onChange={(e) => setForm((f) => ({...f, state: e.target.value}))}
                        className={inputCls}>
                        <option value="">Select...</option>
                        {States.map((s) => (
                            <option key={s.code} value={s.code}>
                                {s.name}
                            </option>
                        ))}
                    </select>
                </Field>
                <Field label="Zip">
                    <input
                        type="text"
                        value={form.zip}
                        onChange={(e) => setForm((f) => ({...f, zip: e.target.value}))}
                        className={inputCls}
                    />
                </Field>
            </div>

            <div className="flex justify-end gap-2 pt-2">
                <Button variant="secondary" onClick={onClose} disabled={isSaving}>
                    Cancel
                </Button>
                <Button variant="primary" onClick={handleSave} loading={isSaving}>
                    Save
                </Button>
            </div>
        </div>
    );
};

const inputCls =
    'w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-mld-primary focus:border-transparent';

const Field: React.FC<{label: string; required?: boolean; children: React.ReactNode}> = ({
    label,
    required,
    children,
}) => (
    <div>
        <label className="mb-1 block text-xs font-medium text-mld-menu-text">
            {label}
            {required ? <span className="text-mld-danger/60"> *</span> : null}
        </label>
        {children}
    </div>
);

export default PatientEditModal;
